import React, { useCallback } from 'react';
import { RefreshControl, View } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';

import { MY_DAY_STEP_STATUS, isDocketDoneForMyDay } from '../../../common/utils/myDay/driverDaySteps';
import Text from '../../components/text/Text';
import {
	ActionDock,
	Card,
	EmptyState,
	Screen,
	SecondaryButton,
	SectionTitle,
	StepRow,
	Tag,
} from '../../design/components';
import { STEP_STATUS } from '../../design/components/StatusIcon';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import useEmployeeDayPreview from '../myDay/useEmployeeDayPreview';
import RunStopsList from './components/RunStopsList';
import RunSwitcher from './components/RunSwitcher';
import { useDriverShell } from './DriverDayProvider';
import { DRIVER_ROUTE } from './driverRoutes';
import {
	getOptionalStepTags,
	getPhaseStepStatus,
	getRequiredStepProgress,
	getSignOffStepStatus,
	getStopStepStatus,
} from './stepStatus';
import { getStopPositionName, getStopSiteName } from './stopDetails';

const PhaseSteps = ({ steps, nextStepKey }) => steps.map((step) => {
	const status = getPhaseStepStatus(step, nextStepKey);
	const signOffStatus = status === STEP_STATUS.DONE ? getSignOffStepStatus(step) : undefined;
	return (
		<StepRow
			key={step.key}
			status={status}
			title={step.label}
			subtitle={step.description}
			statusText={signOffStatus?.statusText}
			statusTone={signOffStatus?.statusTone}
			tags={getOptionalStepTags(step)}
		/>
	);
});

// Same "n of m required steps done" as Start of day and End of day.
const countDone = (steps) => {
	const progress = getRequiredStepProgress(steps);
	return progress.totalCount ? progress.label : undefined;
};

/**
 * Read-only preview of the selected employee's recorded day, in the redesign
 * frame: a next-step card, then Start of day, On the run (stops by site) with
 * the chosen stop's steps, and End of day, all as step rows (status circle on
 * the left). Data comes from useEmployeeDayPreview. Admins who may engage get
 * "Work this run for training" in the dock.
 */
const EmployeeDayPreview = ({ employee, onChooseEmployee, canEngage, onEngage }) => {
	const styles = useThemedStyles(createStyles);
	const {
		isOnline,
		loading,
		error,
		refreshPreview,
		isRefreshing,
		detailedRuns,
		activeRun,
		activeRunId,
		setSelectedRunId,
		setSelectedDocketId,
		sortedDockets,
		activeDocket,
		startSteps,
		endSteps,
		perStopSteps,
		runStarted,
		nextAction,
		nextActionCopy,
		showStopsSection,
		employeeName,
		dateLabel,
		dataWarning,
	} = useEmployeeDayPreview(employee);
	const selectRun = useCallback((runId) => {
		setSelectedRunId(String(runId));
		setSelectedDocketId(null);
	}, [setSelectedDocketId, setSelectedRunId]);
	const selectStop = useCallback(docket => setSelectedDocketId(docket.id), [setSelectedDocketId]);

	const visibleStartSteps = startSteps.filter(step => step.status !== MY_DAY_STEP_STATUS.HIDDEN);
	const visibleEndSteps = endSteps.filter(step => step.status !== MY_DAY_STEP_STATUS.HIDDEN);
	const nextStepKey = ['start', 'end'].includes(nextAction?.type) ? nextAction.step?.key : null;
	const nextStopStepId = perStopSteps.find(step => !step.done && !step.blocked)?.id;
	const doneStopCount = sortedDockets.filter(isDocketDoneForMyDay).length;
	const activeStopName = activeDocket
		? [getStopSiteName(activeDocket), getStopPositionName(activeDocket)].filter(Boolean).join(' · ')
		: '';
	const isLoadFailed = Boolean(error && !activeRun && !loading);

	return (
		<Screen
			title="Employee's day"
			backLabel="Home"
			heading={`${employeeName}'s day`}
			subheading={[dateLabel, activeRun?.route?.name].filter(Boolean).join(' · ')}
			isLoading={loading && !activeRun}
			error={isLoadFailed ? `${employeeName}'s runs could not be loaded. Try again when the connection is available.` : undefined}
			onRetry={refreshPreview}
			refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={refreshPreview} />}
			dock={canEngage && activeRun ? (
				<ActionDock
					hint="Training mode: you work the run, actions are recorded under your account."
					action={{
						label: isOnline ? 'Work this run for training' : 'Connect to work this run',
						onPress: () => onEngage(activeRun.id),
						disabled: !isOnline,
					}}
				/>
			) : null}
		>
			<Card style={styles.notice}>
				<Tag label="Preview · read-only" size="regular" />
				<Text style={styles.mutedText}>
					{'This is the employee\'s recorded day. Run, clock, pick, return and completion actions are unavailable.'}
				</Text>
				<SecondaryButton label="Choose employee" icon="users" onPress={onChooseEmployee} />
			</Card>
			{Boolean(dataWarning) && (
				<Card tone="warning">
					<Text style={styles.warningText}>{dataWarning}</Text>
				</Card>
			)}
			<RunSwitcher runs={detailedRuns} activeRunId={activeRunId} onSelectRun={selectRun} />
			{!activeRun ? (
				<EmptyState title="No runs today" message={`No runs were found for ${employeeName} today.`} />
			) : (
				<>
					<View style={styles.section}>
						<SectionTitle title="Next step" meta="Read-only" />
						<StepRow status={STEP_STATUS.CURRENT} title={nextActionCopy.title} subtitle={nextActionCopy.description} />
					</View>
					<View style={styles.section}>
						<SectionTitle title="Start of day" meta={countDone(visibleStartSteps)} />
						{visibleStartSteps.length
							? <PhaseSteps steps={visibleStartSteps} nextStepKey={nextStepKey} />
							: <Text style={styles.mutedText}>Nothing to prepare for this run.</Text>}
					</View>
					<View style={styles.section}>
						<SectionTitle
							title="On the run"
							meta={sortedDockets.length ? `${doneStopCount} of ${sortedDockets.length} done` : undefined}
						/>
						{!sortedDockets.length ? (
							<Text style={styles.mutedText}>No stops were found for this run.</Text>
						) : !showStopsSection ? (
							<Text style={styles.mutedText}>The stop list appears after required start-of-day preparation is complete.</Text>
						) : (
							<RunStopsList
								dockets={sortedDockets}
								isDocketDone={isDocketDoneForMyDay}
								nowDocketId={activeDocket?.id}
								onOpenStop={selectStop}
							/>
						)}
					</View>
					{showStopsSection && Boolean(activeDocket) && (
						<View style={styles.section}>
							<SectionTitle
								title={isDocketDoneForMyDay(activeDocket) ? 'Selected completed stop' : 'Selected stop'}
								meta={activeStopName}
							/>
							{perStopSteps.map(step => (
								<StepRow
									key={step.key}
									status={getStopStepStatus(step, nextStopStepId)}
									title={step.label}
									subtitle={step.description}
								/>
							))}
						</View>
					)}
					<View style={styles.section}>
						<SectionTitle title="End of day" meta={runStarted ? countDone(visibleEndSteps) : undefined} />
						{runStarted
							? <PhaseSteps steps={visibleEndSteps} nextStepKey={nextStepKey} />
							: <Text style={styles.mutedText}>This section appears after the employee starts the service run.</Text>}
					</View>
				</>
			)}
		</Screen>
	);
};

/**
 * Admin preview of an employee's day, opened from Home ("View an employee's
 * day"). Params: { employee } from EmployeeMyDaySelectorDialog (+ previewScopeKey).
 * Working one of their runs starts training mode and opens Start of day.
 */
const EmployeeDayPreviewView = () => {
	const navigation = useNavigation();
	const route = useRoute();
	const {
		canViewEmployeeDays,
		canEngageEmployeeRuns,
		openEmployeeSelector,
		previewScopeKey,
		startTraining,
	} = useDriverShell();
	const employee = route.params?.employee;

	const engageEmployeeRun = useCallback((runId) => {
		if (startTraining(employee, runId)) {
			navigation.push(DRIVER_ROUTE.START_OF_DAY);
		}
	}, [employee, navigation, startTraining]);

	// A preview from another tenant or user session is never shown.
	const isAvailable = Boolean(canViewEmployeeDays && employee && employee.previewScopeKey === previewScopeKey);

	if (!isAvailable) {
		return (
			<Screen title="Employee's day" backLabel="Home">
				<EmptyState title="Preview unavailable" message="Choose the employee again from Home." />
			</Screen>
		);
	}

	return (
		<EmployeeDayPreview
			key={`${previewScopeKey}:${employee.id}`}
			employee={employee}
			onChooseEmployee={openEmployeeSelector}
			canEngage={canEngageEmployeeRuns}
			onEngage={engageEmployeeRun}
		/>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	notice: {
		gap: space.md,
		alignItems: 'flex-start',
	},
	mutedText: {
		...type.secondary,
		color: colors.textSecondary,
	},
	warningText: {
		...type.bodySmall,
		color: colors.text,
	},
	section: {
		gap: space.sm,
	},
});

export default EmployeeDayPreviewView;
